import React from "react";
import { anyInProgress, fetchFaceLockCrew, FACELOCK_POLL_MS, isAnimatedInProgress, type FaceLockCrew } from "@/lib/facelock";

export type FaceLockCrewState = {
  /** "loading" until the first answer; "off" renders nothing anywhere. */
  status: "loading" | "on" | "off" | "error";
  /** The last good answer. Kept through a failed poll so the grid never blinks. */
  crew: FaceLockCrew | null;
  refresh: () => Promise<void>;
};

/** GET /api/web/facelock/avatars once (and, with `poll`, every 15 s while any
 *  avatar is training, or its picture is being learned or drawn; with `poll: "required"` only while the server says a
 *  FaceLock is required, so a host that merely offers the picker polls as
 *  little as it always did). `animated`: also while an animated picture is
 *  being drawn (the Avatars page, which shows it). `active` false never calls the server. */
export function useFaceLockCrew(active: boolean, opts: { poll?: boolean | "required"; animated?: boolean } = {}): FaceLockCrewState {
  const [status, setStatus] = React.useState<FaceLockCrewState["status"]>("loading");
  const [crew, setCrew] = React.useState<FaceLockCrew | null>(null);
  const alive = React.useRef(true);
  React.useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const refresh = React.useCallback(async () => {
    const load = await fetchFaceLockCrew();
    if (!alive.current) return;
    if (load.state === "on") { setCrew(load.crew); setStatus("on"); return; }
    if (load.state === "off") { setCrew(null); setStatus("off"); return; }
    // A failed poll keeps what is on screen; a failed first load shows nothing.
    setStatus((prev) => (prev === "on" ? "on" : "error"));
  }, []);

  React.useEffect(() => {
    if (!active) { setStatus("off"); setCrew(null); return; }
    void refresh();
  }, [active, refresh]);

  const training = !!crew && (anyInProgress(crew.avatars) || (opts.animated === true && crew.avatars.some(isAnimatedInProgress)));
  const poll = opts.poll === true || (opts.poll === "required" && crew?.required === true);
  React.useEffect(() => {
    if (!active || !poll || !training) return;
    const id = window.setInterval(() => { void refresh(); }, FACELOCK_POLL_MS);
    return () => window.clearInterval(id);
  }, [active, poll, training, refresh]);

  return { status, crew, refresh };
}
